import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { createParser, parseAsStringLiteral, useQueryState, useQueryStates } from "nuqs";
import { useDebounce } from "use-debounce";
import { useLocalStorage } from "usehooks-ts";
import type { RouterOutputs } from "@remi/api";
import { RemiErrorDeprecated } from "@remi/error";
import { useEffect } from "@remi/next-ui";
import { useBreakpoint } from "@remi/next-ui/hooks";
import { InboxSchema, type Inbox as InboxType } from "@remi/types/inbox";
import { Comparator, type SortDefinition } from "@remi/types/query-builder";
import { InboxItemType } from "@remi/types/table";

import {
  type ConditionAction,
  type ConditionState,
  decodeConditions,
  emptyConditionState,
  encodeConditions,
  extractConditionsFromView,
  getDefaultConditions,
  hasActiveConditions,
} from "~/components/tickets/conditions";
import { INBOX_SORTABLE_FIELDS, isInboxSortableField } from "~/components/tickets/inbox-table-sort";
import { type ViewMode, ViewModeSchema } from "~/components/tickets/view-mode";
import { useUserProfile } from "~/contexts/UserProfileContext";
import { resolveInboxItem } from "~/hooks/inbox/resolveInboxItem";
import { useInboxTeamId } from "~/hooks/inbox/useInboxTeamId";
import { useInboxViews } from "~/hooks/inbox/useInboxViews";

const DEFAULT_INBOX_SORT: SortDefinition["sort"][number] = { field: "followUpDate", direction: "ASC" };

interface InheritedScope {
  inbox: InboxType;
  superConditions: ConditionState | undefined;
}

export const resolveInboxConditions = (
  state: ConditionState | null,
  { inbox, superConditions }: InheritedScope,
): ConditionState => {
  const resolved = state ?? emptyConditionState;
  if (resolved.projectCategory === undefined) {
    return resolved;
  }
  // A custom view's inherited type is unknown until the view loads; narrowing in that window would
  // write type=TASK into the URL and empty a Conversations view once it arrives.
  if (inbox === "custom" && superConditions === undefined) {
    return resolved;
  }
  if (superConditions?.type !== undefined && ![superConditions.type.value].flat().includes(InboxItemType.TASK)) {
    return resolved;
  }
  if (resolved.type !== undefined) {
    const types = [resolved.type.value].flat();
    if (!types.includes(InboxItemType.TASK) || types.every((type) => type === InboxItemType.TASK)) {
      return resolved;
    }
  }
  return { ...resolved, type: { field: "type", value: InboxItemType.TASK, comparator: Comparator.EQUALS } };
};

export const conditionsReducer = (
  state: ConditionState,
  action: ConditionAction,
  scope: InheritedScope,
): ConditionState => {
  if (action.type === "UPDATE_CONDITION_FIELD") {
    const next = { ...state, [action.payload.field]: action.payload.condition };
    return action.payload.field === "projectCategory" ? resolveInboxConditions(next, scope) : next;
  }
  if (action.type === "SET_CONDITION_STATE") {
    return action.payload.state;
  }
  return { ...emptyConditionState };
};

export const inboxFiltersParser = createParser<ConditionState>({
  parse: (query) => {
    try {
      // JSON.parse throws SyntaxError on a malformed ?inboxFilters= value; decodeConditions uses
      // safeParse and never throws, so this catch is the only guard against bad JSON.
      return decodeConditions(JSON.parse(query));
    } catch {
      return null;
    }
  },
  serialize: (conditions) => JSON.stringify(encodeConditions(conditions)),
}).withOptions({ shallow: true });

const inboxSortUrlParsers = {
  inboxSort: parseAsStringLiteral(INBOX_SORTABLE_FIELDS).withOptions({ shallow: true }),
  inboxDir: parseAsStringLiteral(["ASC", "DESC"] as const).withOptions({ shallow: true }),
};

export type InboxViewRecord = RouterOutputs["communication"]["listInboxViews"][number];

export interface InboxContextInterface {
  inbox: InboxType;
  view: InboxViewRecord | undefined;
  teamId: string;
  setTeamId: (teamId: string) => void;
  superConditions: ConditionState | undefined;
  conditions: ConditionState;
  dispatchConditions: (action: ConditionAction) => void;
  sort: SortDefinition["sort"][number];
  setSort: (sort: SortDefinition["sort"][number]) => void;
  nextItem: { type: "TICKET" | "CONVERSATION" | "TASK" | "ESCALATION"; id: string } | null;
  setNextItem: (nextItem: { type: "TICKET" | "CONVERSATION" | "TASK" | "ESCALATION"; id: string } | null) => void;
  isMaxSmScreen: boolean;
  hasSelection: boolean;
  viewMode: ViewMode;
  setViewMode: (viewMode: ViewMode) => void;
}

export const InboxContext = createContext<InboxContextInterface | null>(null);

export const useInboxContext = () => {
  const inboxContext = useContext(InboxContext);
  if (inboxContext === null) {
    throw new RemiErrorDeprecated({ message: "useInboxContext may only be called within <InboxContext />" });
  }
  return inboxContext;
};

export const InboxContextProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const { user } = useUserProfile();
  const [storedTeamId, setTeamId] = useLocalStorage("inbox-team-id", "remi_customer_success_user_group");
  const teamId = useInboxTeamId(storedTeamId);
  const [viewId] = useQueryState("viewId");
  const [debouncedViewId] = useDebounce(viewId, 200);

  const { data: views = [], isPending: viewsPending, isFetching: viewsFetching } = useInboxViews({ teamIds: [teamId] });
  const inbox = InboxSchema.catch("my-inbox").parse(router.query.inbox as unknown);
  const isMaxSmScreen = useBreakpoint("maxSm");

  const view = useMemo(
    () => (inbox === "custom" && debouncedViewId ? views.find((v) => v.id === debouncedViewId) : undefined),
    [views, debouncedViewId, inbox],
  );

  // Synchronously derived: consumers issue their queries from the first render.
  const superConditions = useMemo(() => {
    if (inbox === "custom") {
      // undefined, not an empty state: an empty condition state here is an unfiltered org-wide query.
      return view ? extractConditionsFromView(view) : undefined;
    }
    return getDefaultConditions({ inbox, personId: user.person.id, currentTeamId: teamId });
  }, [view, inbox, user.person.id, teamId]);

  const [urlConditions, setUrlConditions] = useQueryState("inboxFilters", inboxFiltersParser);
  const conditions = useMemo(
    () => resolveInboxConditions(urlConditions, { inbox, superConditions }),
    [urlConditions, inbox, superConditions],
  );

  const dispatchConditions = useCallback(
    (action: ConditionAction) => {
      void setUrlConditions((current) => {
        const scope = { inbox, superConditions };
        const next = conditionsReducer(resolveInboxConditions(current, scope), action, scope);
        return hasActiveConditions(next) ? next : null;
      });
    },
    [setUrlConditions, inbox, superConditions],
  );

  const [{ inboxSort, inboxDir }, setSortParams] = useQueryStates(inboxSortUrlParsers);

  const sort = useMemo<SortDefinition["sort"][number]>(() => {
    if (inboxSort) {
      return { field: inboxSort, direction: inboxDir ?? "ASC" };
    }
    return view?.sort.sort[0] ?? DEFAULT_INBOX_SORT;
  }, [inboxSort, inboxDir, view]);

  const setSort = useCallback(
    (next: SortDefinition["sort"][number]) => {
      if (!isInboxSortableField(next.field)) {
        void setSortParams({ inboxSort: null, inboxDir: null });
        return;
      }
      void setSortParams({ inboxSort: next.field, inboxDir: next.direction });
    },
    [setSortParams],
  );

  // Compare the route's own viewId, not the nuqs copy: nuqs lags router.query by a render, so
  // mid-navigation it and its debounce read stale-equal and a still-resolving view looks unresolvable.
  const routeViewId = typeof router.query.viewId === "string" ? router.query.viewId : null;
  // isPending goes false on the IndexedDB-restored snapshot, so without isFetching a view created
  // since that snapshot redirects away before the always-stale mount refetch delivers it.
  const viewsSettled = !viewsPending && !viewsFetching;
  const viewUnresolvable = inbox === "custom" && viewsSettled && routeViewId === debouncedViewId && view === undefined;

  // Keying the redirect on router identity would re-fire it mid-navigation, when router changes but
  // viewUnresolvable has not yet gone false.
  const routerRef = useRef(router);
  useEffect(() => {
    routerRef.current = router;
  });

  useEffect(() => {
    if (viewUnresolvable) {
      void routerRef.current.replace("/inbox/my-inbox");
    }
  }, [viewUnresolvable]);

  const [nextItem, setNextItem] = useState<{
    type: "TICKET" | "CONVERSATION" | "TASK" | "ESCALATION";
    id: string;
  } | null>(null);

  const hasSelection = resolveInboxItem(router.query) !== null;

  const [storedViewMode, setStoredViewMode] = useLocalStorage("inbox-view-mode", "list");
  const setViewMode = useCallback((mode: ViewMode) => setStoredViewMode(mode), [setStoredViewMode]);
  const viewMode = isMaxSmScreen ? "list" : ViewModeSchema.catch("list").parse(storedViewMode as unknown);

  const context = useMemo(
    () => ({
      inbox,
      view,
      teamId,
      setTeamId,
      superConditions,
      conditions,
      dispatchConditions,
      sort,
      setSort,
      nextItem,
      setNextItem,
      isMaxSmScreen,
      hasSelection,
      viewMode,
      setViewMode,
    }),
    [
      inbox,
      view,
      teamId,
      setTeamId,
      superConditions,
      conditions,
      dispatchConditions,
      sort,
      setSort,
      nextItem,
      isMaxSmScreen,
      hasSelection,
      viewMode,
      setViewMode,
    ],
  );

  return <InboxContext.Provider value={context}>{children}</InboxContext.Provider>;
};
